import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';
import classnames from 'classnames';
import { trackHomePageFeaturedSearch, trackHomePageNavigation } from 'lib/Tracking';
import Heading from 'components/Home/Heading';
import { colors, screen } from 'components/styles/variables';
import { BodyTypeMessages, BodyTypeValue } from 'lib/bodyType';

// assets
import sedan from 'public/static/images/body_styles/sedan.jpg';
import sedanAngled from 'public/static/images/body_styles/sedan-angled.jpg';
import coupe from 'public/static/images/body_styles/coupe.jpg';
import coupeAngled from 'public/static/images/body_styles/coupe-angled.jpg';
import convertible from 'public/static/images/body_styles/convertible.jpg';
import convertibleAngled from 'public/static/images/body_styles/convertible-angled.jpg';
import wagon from 'public/static/images/body_styles/wagon.jpg';
import wagonAngled from 'public/static/images/body_styles/wagon-angled.jpg';
import truck from 'public/static/images/body_styles/truck.jpg';
import truckAngled from 'public/static/images/body_styles/truck-angled.jpg';
import suv from 'public/static/images/body_styles/suv.jpg';
import suvAngled from 'public/static/images/body_styles/suv-angled.jpg';
import minivan from 'public/static/images/body_styles/minivan.jpg';
import minivanAngled from 'public/static/images/body_styles/minivan-angled.jpg';
import crossover from 'public/static/images/body_styles/crossover.jpg';
import crossoverAngled from 'public/static/images/body_styles/crossover-angled.jpg';
import Ad from 'lib/GoogleAds/SuggestiveSellingAds/Ad';
import bodyTypesSearchesCss from './styles';

const BODY_TYPES = Object.freeze([
  {
    i18nKey: BodyTypeMessages[BodyTypeValue.Sedan],
    value: BodyTypeValue.Sedan,
    image: sedan,
    angledImage: sedanAngled,
  },
  {
    i18nKey: BodyTypeMessages[BodyTypeValue.Coupe],
    value: BodyTypeValue.Coupe,
    image: coupe,
    angledImage: coupeAngled,
  },
  {
    i18nKey: BodyTypeMessages[BodyTypeValue.Wagon],
    value: BodyTypeValue.Wagon,
    image: wagon,
    angledImage: wagonAngled,
  },
  {
    i18nKey: BodyTypeMessages[BodyTypeValue.Convertible],
    value: BodyTypeValue.Convertible,
    image: convertible,
    angledImage: convertibleAngled,
  },
  {
    i18nKey: BodyTypeMessages[BodyTypeValue.Truck],
    value: 'pickup+truck',
    image: truck,
    angledImage: truckAngled,
  },
  {
    i18nKey: BodyTypeMessages[BodyTypeValue.SUV],
    value: BodyTypeValue.SUV,
    image: suv,
    angledImage: suvAngled,
  },
  {
    i18nKey: BodyTypeMessages[BodyTypeValue.Minivan],
    value: BodyTypeValue.Minivan,
    image: minivan,
    angledImage: minivanAngled,
  },
  {
    i18nKey: BodyTypeMessages[BodyTypeValue.Crossover],
    value: BodyTypeValue.Crossover,
    image: crossover,
    angledImage: crossoverAngled,
  },
]);

const BodyTypesSearches = (): JSX.Element => {
  const { t } = useTranslation();

  return (
    <div id="body-styles" className="body-type">
      <Heading title={t('home.bodyStyle.title')} />
      <div className="body-styles-container">
        {BODY_TYPES.map((type) => (
          <BodyTypeCard
            key={type.value}
            {...type}
            label={t(type.i18nKey)}
            href={`/${type.value}`}
          />
        ))}
      </div>
      <style jsx>{bodyTypesSearchesCss}</style>
    </div>
  );
};

const Subscript = ({ name }: { name: string }): JSX.Element => (
  <span className="subscript">
    {name}
    <style jsx>
      {`
        .subscript {
          font-size: 11px;
          text-decoration: none;
          font-family: Arial, sans-serif;
          font-weight: bold;
          text-align: center;
          line-height: 9px;
          vertical-align: baseline;
          display: block;
          color: ${colors.ALR2};
        }
      `}
    </style>
  </span>
);

interface BodyTypeCardProps {
  value: string;
  image: string;
  angledImage: string;
  label: string;
  href: string;
  ad?: Ad;
}

function BodyTypeCard({
  value,
  image: ourImage,
  angledImage: ourAngledImage,
  label,
  href: ourHref,
  ad,
}: BodyTypeCardProps): JSX.Element {
  const [viewRef, inView] = useInView({ triggerOnce: true });
  const image = ad ? ad.image.left : ourImage;
  const angledImage = ad ? ad.image.leftQtr : ourAngledImage;
  const href = ad ? ad.url : ourHref;

  return (
    <a
      href={href}
      data-body-type-search={value}
      data-testid="body-type-card-anchor"
      className="body-card"
      onClick={(): void => {
        trackHomePageFeaturedSearch('featured_body_types', `/${value}`);
        trackHomePageNavigation();
      }}
    >
      <img src={angledImage} loading="lazy" className="preloader" />
      <div
        ref={viewRef}
        data-image={value}
        className={classnames('image', { 'image-in-view': inView })}
        role="img"
        aria-label={value}
      />
      <label className="label">
        {label} {ad ? <Subscript name={ad.vehicleName} /> : null}
      </label>
      <style jsx>
        {`
          .preloader {
            display: none;
          }

          .body-card {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: flex-start;
          }

          .image {
            width: 100%;
            height: 100px;
            background-size: contain;
            background-repeat: no-repeat;
            background-position: center;
          }

          .image-in-view {
            background-image: url(${image});
          }

          .image:hover {
            background-image: url(${angledImage});
          }

          .label {
            margin-top: -20px;
            text-align: center;
            color: ${colors.BLACK};
            font-size: 14px;
            font-weight: 600;
            height: 50px;
            width: 100%;
          }

          @media screen and (min-width: ${screen.smMin}) and (max-width: ${screen.smMax}) {
            .label {
              font-size: 16px;
            }
          }

          @media screen and (min-width: ${screen.mdMin}) {
            .label {
              font-size: 20px;
            }

            .image {
              height: 150px;
              width: 200px;
            }
          }
        `}
      </style>
    </a>
  );
}

export default BodyTypesSearches;
